import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import BookingPageWrapper from './components/booking/BookingPageContainer';
import AdminPanel from './pages/AdminPanel';
import ProfessionalPanel from './pages/ProfessionalPanel';
import PwaUpdateNotifier from './components/PwaUpdateNotifier';
import AccountCreationFlow from './components/onboarding/AccountCreationFlow';
import SaasAdminPanel from './pages/SaasAdminPanel';
import { ToastProvider } from './components/ui/ToastManager';
import NotFoundPage from './pages/NotFoundPage';
import WebFont from 'webfontloader';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { SalonProvider } from './contexts/SalonContext';
import { AppointmentProvider } from './contexts/AppointmentContext';
import { ServiceProvider } from './contexts/ServiceContext';
import { ProfessionalProvider } from './contexts/ProfessionalContext';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ConfirmPage from './pages/appointment/ConfirmPage';
import ReschedulePage from './pages/appointment/ReschedulePage';
import CancelPage from './pages/appointment/CancelPage';
import SalonSelector from './components/SalonSelector';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import LoginPage from './pages/LoginPage';
import SalonSelectionPage from './pages/SalonSelectionPage';
import SimpleMobileCalendar from './components/Calendar';
import BookingWrapper from './components/booking/BookingWrapper';

// Componentes temporários definidos inline no topo para conformidade com ESLint
const ExampleLanding = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-700 flex flex-col items-center justify-center p-6 text-white">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">
          Sistema de Agendamento para Salões de Beleza
        </h1>
        <p className="text-xl mb-8">
          Ofereça um sistema moderno de agendamentos online para seus clientes
        </p>
        
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-6">
          <button 
            onClick={() => navigate('/login')}
            className="px-8 py-3 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          >
            Entrar
          </button>
          
          <button 
            onClick={() => navigate('/signup')}
            className="px-8 py-3 bg-transparent border-2 border-white text-white font-medium rounded-lg shadow-md hover:bg-white hover:bg-opacity-10 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          >
            Criar conta
          </button>
        </div>
      </div>
    </div>
  );
};

const SalonInfoPage = () => <div className="p-8">Informações do Salão</div>;
const AppointmentSuccess = () => <div className="p-8">Agendamento realizado com sucesso!</div>;
const AppointmentConfirmation = () => <div className="p-8">Confirmação de Agendamento</div>;

// Provider de tema simulado até que seja implementado
const ThemeProvider = ({ children }) => <>{children}</>;

// Provider de configurações simulado até que seja implementado
const SettingsProvider = ({ salonId, children }) => <>{children}</>;

// Provider de alertas simulado até que seja implementado
const AlertProvider = ({ children }) => <>{children}</>;

// Componente principal
const App = () => {
  // Carregar as fontes do Google
  useEffect(() => {
    WebFont.load({
      google: {
        families: [
          'Roboto:400,500,700',
          'Open Sans:400,600,700',
          'Lato:400,700',
          'Montserrat:400,500,700',
          'Poppins:400,500,700',
          'Raleway:400,500,700',
          'Dancing Script:400,700',
          'Playfair Display:400,700'
        ]
      }
    });
  }, []);

  return (
    <ToastProvider>
      <AuthProvider>
        <UserProvider>
          <ThemeProvider>
            <AlertProvider>
              <Router>
                <Suspense fallback={<LoadingFallback />}>
                  <SalonSelector />
                  <Routes>
                    {/* Rota principal (redirecionar para landing) */}
                    <Route path="/" element={<Navigate to="/landing" replace />} />
                    
                    {/* Login page */}
                    <Route path="/login" element={<LoginPage />} />
                    
                    {/* Seleção de salão */}
                    <Route path="/salon-selection" element={<SalonSelectionPage />} />
                    
                    {/* Landing page */}
                    <Route path="/landing" element={<ExampleLanding />} />
                    
                    {/* Rotas para salões específicos */}
                    <Route path="/salon/:salonId/*" element={
                      <SalonRoutes />
                    } />
                    
                    {/* Rotas para administração */}
                    <Route path="/admin/:salonId/*" element={
                      <AdminRoutes />
                    } />
                    
                    {/* Rota para o painel de profissionais */}
                    <Route path="/professional/*" element={
                      <ProfessionalPanelWrapper />
                    } />
                    
                    {/* Rota para criação de contas */}
                    <Route path="/signup" element={<AccountCreationFlow />} />
                    
                    {/* Rotas para convites */}
                    <Route path="/accept-invite" element={<AcceptInvitePage />} />
                    <Route path="/accept-invite/:token" element={<InvitationAcceptPage />} />
                    
                    {/* Rotas para agendamento - versão otimizada sem contextos desnecessários */}
                    <Route 
                      path="/:salonId" 
                      element={
                        <PublicBookingRoute />
                      } 
                    />
                    <Route 
                      path="/:salonId/admin" 
                      element={
                        <SalonContextWrapper>
                          <AdminPanel />
                        </SalonContextWrapper>
                      } 
                    />
                    
                    {/* Novas rotas para ações de agendamento via links */}
                    <Route path="/appointment/confirm/:appointmentId" element={<ConfirmPage />} />
                    <Route path="/appointment/reschedule/:appointmentId" element={
                      <Suspense fallback={<div>Carregando...</div>}>
                        <ReschedulePage />
                      </Suspense>
                    } />
                    <Route path="/appointment/cancel/:appointmentId" element={
                      <Suspense fallback={<div>Carregando...</div>}>
                        <CancelPage />
                      </Suspense>
                    } />
                    
                    {/* Rota para o painel de administração do SaaS */}
                    <Route path="/saas-admin" element={<SaasAdminPanel />} />
                    
                    {/* Página não encontrada */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
                {/* Componente de notificação de atualização PWA */}
                <PwaUpdateNotifier />
              </Router>
            </AlertProvider>
          </ThemeProvider>
        </UserProvider>
      </AuthProvider>
      {/* ReactQueryDevtools para depuração no ambiente de desenvolvimento */}
      {process.env.NODE_ENV !== 'production' && <ReactQueryDevtools initialIsOpen={false} />}
    </ToastProvider>
  );
};

// Componente wrapper para contextos específicos do salão
const SalonContextWrapper = ({ children }) => {
  // Pegamos o ID do salão da URL
  const salonId = window.location.pathname.split('/')[1];
  
  return (
    <SalonProvider salonId={salonId}>
      <ServiceProvider salonId={salonId}>
        <ProfessionalProvider salonId={salonId}>
          <AppointmentProvider salonId={salonId}>
            {children}
          </AppointmentProvider>
        </ProfessionalProvider>
      </ServiceProvider>
    </SalonProvider>
  );
};

// Componente otimizado para rota de agendamento público
const PublicBookingRoute = () => {
  const { salonId } = useParams();
  return <BookingWrapper salonId={salonId} />;
};

// Componente de Loader
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Componente que gerencia as rotas relacionadas a um salão específico
const SalonRoutes = () => {
  const { salonId } = useParams();
  const [loading, setLoading] = useState(true);
  const [salonExists, setSalonExists] = useState(false);
  
  // Verificar se o salão existe antes de renderizar as rotas
  useEffect(() => {
    const checkSalon = async () => {
      try {
        // Consultar o Firestore para verificar se o salão existe
        const salonRef = doc(db, 'salons', salonId);
        const salonSnapshot = await getDoc(salonRef);
        
        setSalonExists(salonSnapshot.exists());
        setLoading(false);
      } catch (error) {
        console.error('Erro ao verificar salão:', error);
        setSalonExists(false);
        setLoading(false);
      }
    };
    
    if (salonId) {
      checkSalon();
    }
  }, [salonId]);
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  if (!salonExists) {
    return <Navigate to="/landing" replace />;
  }
  
  return (
    <SalonProvider salonId={salonId}>
      <SettingsProvider salonId={salonId}>
        <ServiceProvider salonId={salonId}>
          <ProfessionalProvider salonId={salonId}>
            <AppointmentProvider salonId={salonId}>
              <Routes>
                <Route index element={<SalonInfoPage />} />
                <Route path="booking" element={<BookingWrapper salonId={salonId} />} />
                <Route path="success/:appointmentId" element={<AppointmentSuccess />} />
                <Route path="confirmation/:appointmentId" element={<AppointmentConfirmation />} />
                <Route path="*" element={<Navigate to={`/salon/${salonId}`} replace />} />
              </Routes>
            </AppointmentProvider>
          </ProfessionalProvider>
        </ServiceProvider>
      </SettingsProvider>
    </SalonProvider>
  );
};

// Componente wrapper para o painel do profissional
const ProfessionalPanelWrapper = () => {
  const { 
    loading: userLoading, 
    refreshUserData, 
    userSalons,
    selectedSalonId,
    getCurrentRole,
    isInitialized
  } = useUser();
  const { isAuthenticated, loading: authLoading, currentUser: authUser } = useAuth();
  const navigate = useNavigate();
  const [accessVerified, setAccessVerified] = useState(false);
  
  // Log estado inicial ao montar
  console.log(`[ProfessionalPanelWrapper] Montando componente:
    - authUser: ${authUser ? `${authUser.uid} (${authUser.email})` : 'não disponível'}
    - isAuthenticated: ${isAuthenticated}
    - authLoading: ${authLoading}
    - userLoading: ${userLoading}
    - isInitialized: ${isInitialized}
    - selectedSalonId: ${selectedSalonId || 'não disponível'}
    - accessVerified: ${accessVerified}
    - userSalons: ${userSalons?.length || 0}
  `);
  
  // Verificar acesso uma única vez quando os dados estiverem prontos
  useEffect(() => {
    let isMounted = true;
    
    // Aguardar inicialização completa e verificar acesso
    if (isInitialized && !userLoading && !authLoading && !accessVerified) {
      const verifyAccess = async () => {
        console.log(`[ProfessionalPanelWrapper] Verificando acesso: isAuthenticated=${isAuthenticated}, authUser=${!!authUser}, selectedSalonId=${selectedSalonId}`);
        
        // Usuário não autenticado - verificar diretamente do AuthContext
        if (!isAuthenticated || !authUser) {
          console.log("[ProfessionalPanelWrapper] Usuário não autenticado, redirecionando para login");
          if (isMounted) {
            navigate('/login', { replace: true });
          }
          return;
        }
        
        // Já verificamos o acesso, não repetir
        if (accessVerified) {
          return;
        }
        
        try {
          // Verificar se precisa atualizar dados
          if (!userSalons || userSalons.length === 0) {
            console.log("[ProfessionalPanelWrapper] Atualizando dados do usuário");
            await refreshUserData(true); // Forçar atualização
          }
          
          if (!isMounted) return;
          
          // Verificar se temos um salão selecionado
          if (!selectedSalonId) {
            console.log("[ProfessionalPanelWrapper] Nenhum salão selecionado, redirecionando para seleção de salão");
            navigate('/salon-selection', { replace: true });
            return;
          }
          
          // Verificar papel do usuário
          const role = getCurrentRole(selectedSalonId);
          console.log(`[ProfessionalPanelWrapper] Papel do usuário no salão ${selectedSalonId}: ${role}`);
          
          // Redirecionar se não for profissional
          if (role !== 'professional') {
            console.log(`[ProfessionalPanelWrapper] Usuário não é profissional, redirecionando`);
            if (['owner', 'admin', 'manager'].includes(role)) {
              navigate(`/admin/${selectedSalonId}/appointments`, { replace: true });
            } else {
              navigate('/salon-selection', { replace: true });
            }
            return;
          }
          
          // Se chegou até aqui, acesso verificado com sucesso
          if (isMounted) {
            console.log("[ProfessionalPanelWrapper] Acesso verificado com sucesso");
            setAccessVerified(true);
          }
        } catch (error) {
          console.error("[ProfessionalPanelWrapper] Erro ao verificar acesso:", error);
          if (isMounted) {
            navigate('/login', { replace: true });
          }
        }
      };
      
      verifyAccess();
    }
    
    return () => {
      isMounted = false;
    };
  }, [isInitialized, userLoading, authLoading, isAuthenticated, authUser, selectedSalonId, userSalons, refreshUserData, getCurrentRole, navigate, accessVerified]);
  
  // Mostrar loading enquanto verifica acesso
  if (userLoading || authLoading || !isInitialized || !accessVerified) {
    return <LoadingFallback />;
  }
  
  console.log(`[ProfessionalPanelWrapper] Renderizando painel para ${authUser.email} no salão ${selectedSalonId}`);
  
  // Quando acesso verificado, renderizar o painel
  return (
    <SalonProvider salonId={selectedSalonId}>
      <ServiceProvider salonId={selectedSalonId}>
        <ProfessionalProvider salonId={selectedSalonId}>
          <AppointmentProvider salonId={selectedSalonId}>
            <ProfessionalPanel />
          </AppointmentProvider>
        </ProfessionalProvider>
      </ServiceProvider>
    </SalonProvider>
  );
};

// Componente que gerencia as rotas de administração
const AdminRoutes = () => {
  const { salonId } = useParams();
  const [loading, setLoading] = useState(true);
  const [salonExists, setSalonExists] = useState(false);
  
  // Verificar se o salão existe antes de renderizar as rotas de administração
  useEffect(() => {
    const checkSalon = async () => {
      try {
        // Consultar o Firestore para verificar se o salão existe
        const salonRef = doc(db, 'salons', salonId);
        const salonSnapshot = await getDoc(salonRef);
        
        setSalonExists(salonSnapshot.exists());
        setLoading(false);
      } catch (error) {
        console.error('Erro ao verificar salão:', error);
        setSalonExists(false);
        setLoading(false);
      }
    };
    
    if (salonId) {
      checkSalon();
    }
  }, [salonId]);
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  if (!salonExists) {
    return <Navigate to="/landing" replace />;
  }
  
  return (
    <SalonProvider salonId={salonId}>
      <SettingsProvider salonId={salonId}>
        <ServiceProvider salonId={salonId}>
          <ProfessionalProvider salonId={salonId}>
            <AppointmentProvider salonId={salonId}>
              <AdminPanel />
            </AppointmentProvider>
          </ProfessionalProvider>
        </ServiceProvider>
      </SettingsProvider>
    </SalonProvider>
  );
};

export default App; 

